/**
 * Canonical financial metric registry.
 *
 * Source labels live here only. Comparison configs and charts must refer to
 * stable metric IDs — never raw JSON path strings or statement labels.
 *
 * Direct metrics read statement cells via sourcePath arrays.
 * Calculated metrics are defined by input IDs + a compute kind evaluated
 * in financialMetricResolver (keeps this file free of circular imports).
 *
 * reportedFcf prefers cashFlow["Free Cash Flow"] when present. Screener /
 * Indian presentation is net-capex FCF: CFO + Fixed assets purchased +
 * Fixed assets sold (purchases are stored as negative outflows).
 *
 * Screener Operating Profit is PBDIT (before depreciation). EBIT is
 * Operating Profit − Depreciation. EBITDA for CFO conversion is Operating Profit.
 */

/** @typedef {'direct' | 'calculated' | 'apiRatio'} MetricType */
/** @typedef {'currency' | 'percentage' | 'multiple' | 'perShare'} MetricUnit */
/** @typedef {'sum' | 'subtract' | 'absMinus' | 'ratio' | 'ratioAbsNumerator' | 'ratioAbsDenominator' | 'ratioPercent' | 'ratioAbsNumeratorPercent' | 'ratioPositiveDenominator'} ComputeKind */

/**
 * @typedef {Object} DirectMetric
 * @property {string} id
 * @property {'direct'} type
 * @property {string} label
 * @property {string} [shortLabel]
 * @property {MetricUnit} unit
 * @property {string[]} sourcePath
 * @property {string} [statement]
 * @property {string} [signConvention]
 * @property {string} [displayTransform]
 * @property {string} [warning]
 * @property {string} [definition]
 * @property {boolean} [auditOnly]
 * @property {{ compute: ComputeKind, inputs: string[] }} [fallback]
 */

/**
 * @typedef {Object} CalculatedMetric
 * @property {string} id
 * @property {'calculated'} type
 * @property {string} label
 * @property {string} [shortLabel]
 * @property {MetricUnit} unit
 * @property {string[]} inputs
 * @property {ComputeKind} compute
 * @property {string} [explanation]
 * @property {string} [warning]
 */

/**
 * @typedef {Object} ApiRatioMetric
 * @property {string} id
 * @property {'apiRatio'} type
 * @property {string} label
 * @property {MetricUnit} unit
 * @property {string} ratioName
 * @property {string} [warning]
 */

/** @type {Record<string, DirectMetric | CalculatedMetric | ApiRatioMetric>} */
export const FINANCIAL_METRICS = {
    // --- Profit and Loss (direct) ---
    revenue: {
        id: 'revenue',
        type: 'direct',
        label: 'Revenue',
        unit: 'currency',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Sales'],
    },
    expenses: {
        id: 'expenses',
        type: 'direct',
        label: 'Expenses',
        unit: 'currency',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Expenses'],
    },
    operatingProfit: {
        id: 'operatingProfit',
        type: 'direct',
        label: 'Operating Profit',
        unit: 'currency',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Operating Profit'],
    },
    operatingMargin: {
        id: 'operatingMargin',
        type: 'direct',
        label: 'Operating Profit Margin',
        unit: 'percentage',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'OPM %'],
    },
    otherIncome: {
        id: 'otherIncome',
        type: 'direct',
        label: 'Other Income',
        unit: 'currency',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Other Income'],
    },
    interestExpense: {
        id: 'interestExpense',
        type: 'direct',
        label: 'Interest',
        unit: 'currency',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Interest'],
    },
    depreciation: {
        id: 'depreciation',
        type: 'direct',
        label: 'Depreciation',
        unit: 'currency',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Depreciation'],
    },
    /**
     * Screener Operating Profit is PBDIT (before D&A). Indian EBIT is OP − Depreciation.
     */
    ebit: {
        id: 'ebit',
        type: 'calculated',
        label: 'EBIT',
        unit: 'currency',
        inputs: ['operatingProfit', 'depreciation'],
        compute: 'subtract',
        explanation: 'Operating Profit − Depreciation. Screener Operating Profit is already before depreciation.',
    },
    /**
     * Legacy OP + Depreciation construct. Do not use as Indian EBITDA —
     * Screener Operating Profit is already PBDIT. Kept for older P&L paths.
     */
    ebitda: {
        id: 'ebitda',
        type: 'calculated',
        label: 'EBITDA',
        unit: 'currency',
        inputs: ['operatingProfit', 'depreciation'],
        compute: 'sum',
        explanation: 'Operating Profit + Depreciation. Not Indian/Screener EBITDA (that is Operating Profit itself).',
    },
    profitBeforeTax: {
        id: 'profitBeforeTax',
        type: 'direct',
        label: 'Profit Before Tax',
        unit: 'currency',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Profit before tax'],
    },
    taxRate: {
        id: 'taxRate',
        type: 'direct',
        label: 'Tax Rate',
        unit: 'percentage',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Tax %'],
    },
    netProfit: {
        id: 'netProfit',
        type: 'direct',
        label: 'Net Profit',
        unit: 'currency',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Net Profit'],
    },
    eps: {
        id: 'eps',
        type: 'direct',
        label: 'EPS',
        unit: 'perShare',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'EPS in Rs'],
    },
    dividendPayoutRatio: {
        id: 'dividendPayoutRatio',
        type: 'direct',
        label: 'Dividend Payout Ratio',
        unit: 'percentage',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Dividend Payout %'],
    },
    salesGrowth: {
        id: 'salesGrowth',
        type: 'direct',
        label: 'Sales Growth',
        unit: 'percentage',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Sales Growth %'],
    },
    materialCostPct: {
        id: 'materialCostPct',
        type: 'direct',
        label: 'Material Cost',
        unit: 'percentage',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Material Cost %'],
    },
    manufacturingCostPct: {
        id: 'manufacturingCostPct',
        type: 'direct',
        label: 'Manufacturing Cost',
        unit: 'percentage',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Manufacturing Cost %'],
    },
    employeeCostPct: {
        id: 'employeeCostPct',
        type: 'direct',
        label: 'Employee Cost',
        unit: 'percentage',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Employee Cost %'],
    },
    otherCostPct: {
        id: 'otherCostPct',
        type: 'direct',
        label: 'Other Cost',
        unit: 'percentage',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Other Cost %'],
    },
    exceptionalItems: {
        id: 'exceptionalItems',
        type: 'direct',
        label: 'Exceptional Items',
        unit: 'currency',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Exceptional items'],
    },
    normalizedOtherIncome: {
        id: 'normalizedOtherIncome',
        type: 'direct',
        label: 'Normalized Other Income',
        unit: 'currency',
        statement: 'profitAndLoss',
        sourcePath: ['profitAndLoss', 'Other income normal'],
    },

    // --- Balance Sheet (direct) ---
    equityCapital: {
        id: 'equityCapital',
        type: 'direct',
        label: 'Equity Capital',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Equity Capital'],
    },
    reserves: {
        id: 'reserves',
        type: 'direct',
        label: 'Reserves',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Reserves'],
    },
    borrowings: {
        id: 'borrowings',
        type: 'direct',
        label: 'Borrowings',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Borrowings'],
    },
    longTermBorrowings: {
        id: 'longTermBorrowings',
        type: 'direct',
        label: 'Long-Term Borrowings',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Long term Borrowings'],
    },
    shortTermBorrowings: {
        id: 'shortTermBorrowings',
        type: 'direct',
        label: 'Short-Term Borrowings',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Short term Borrowings'],
    },
    leaseLiabilities: {
        id: 'leaseLiabilities',
        type: 'direct',
        label: 'Lease Liabilities',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Lease Liabilities'],
    },
    totalLiabilities: {
        id: 'totalLiabilities',
        type: 'direct',
        label: 'Total Liabilities',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Total Liabilities'],
    },
    fixedAssets: {
        id: 'fixedAssets',
        type: 'direct',
        label: 'Fixed Assets',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Fixed Assets'],
    },
    cwip: {
        id: 'cwip',
        type: 'direct',
        label: 'Capital Work in Progress',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'CWIP'],
    },
    investments: {
        id: 'investments',
        type: 'direct',
        label: 'Investments',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Investments'],
    },
    totalAssets: {
        id: 'totalAssets',
        type: 'direct',
        label: 'Total Assets',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Total Assets'],
    },
    inventoryBalance: {
        id: 'inventoryBalance',
        type: 'direct',
        label: 'Inventories',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Inventories'],
    },
    tradeReceivablesBalance: {
        id: 'tradeReceivablesBalance',
        type: 'direct',
        label: 'Trade Receivables',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Trade receivables'],
    },
    cashEquivalents: {
        id: 'cashEquivalents',
        type: 'direct',
        label: 'Cash Equivalents',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Cash Equivalents'],
    },
    otherLiabilities: {
        id: 'otherLiabilities',
        type: 'direct',
        label: 'Other Liabilities',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Other Liabilities'],
        definition: 'Screener "Other Liabilities" line, used as the current-liabilities proxy in Cash Flow Coverage.',
    },
    tradePayablesBalance: {
        id: 'tradePayablesBalance',
        type: 'direct',
        label: 'Trade Payables',
        unit: 'currency',
        statement: 'balanceSheet',
        sourcePath: ['balanceSheet', 'Trade Payables'],
    },

    // --- Cash Flow (direct) ---
    cfo: {
        id: 'cfo',
        type: 'direct',
        label: 'Cash from Operating Activity',
        shortLabel: 'CFO',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Cash from Operating Activity'],
        signConvention: 'inflowPositive',
    },
    cfi: {
        id: 'cfi',
        type: 'direct',
        label: 'Cash from Investing Activity',
        shortLabel: 'CFI',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Cash from Investing Activity'],
        signConvention: 'inflowPositive',
        warning: 'CFI contains capex, investments, asset-sale proceeds and other investing items. It is not the direct FCF deduction.',
    },
    cff: {
        id: 'cff',
        type: 'direct',
        label: 'Cash from Financing Activity',
        shortLabel: 'CFF',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Cash from Financing Activity'],
        signConvention: 'inflowPositive',
    },
    netCashFlow: {
        id: 'netCashFlow',
        type: 'direct',
        label: 'Net Cash Flow',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Net Cash Flow'],
    },
    reportedFcf: {
        id: 'reportedFcf',
        type: 'direct',
        label: 'Free Cash Flow',
        shortLabel: 'FCF',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Free Cash Flow'],
        definition: 'Reported net-capex free cash flow. CFO plus fixed-assets purchased plus fixed-assets sold.',
        fallback: {
            compute: 'sum',
            inputs: ['cfo', 'fixedAssetsPurchased', 'fixedAssetsSold'],
        },
    },
    reportedCfoOp: {
        id: 'reportedCfoOp',
        type: 'direct',
        label: 'Reported CFO / Operating Profit',
        unit: 'percentage',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'CFO/OP'],
        auditOnly: true,
        warning: 'Show only as a reported source metric. Do not rely on it without reconciling its denominator to the current financial-statement definition.',
    },
    fixedAssetsPurchased: {
        id: 'fixedAssetsPurchased',
        type: 'direct',
        label: 'Fixed Assets Purchased',
        shortLabel: 'Gross Capex',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Fixed assets purchased'],
        signConvention: 'outflowNegative',
        displayTransform: 'absoluteValueForCapexComparison',
    },
    fixedAssetsSold: {
        id: 'fixedAssetsSold',
        type: 'direct',
        label: 'Fixed Assets Sold',
        shortLabel: 'Asset Sale Proceeds',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Fixed assets sold'],
        signConvention: 'inflowPositive',
    },
    investmentsPurchased: {
        id: 'investmentsPurchased',
        type: 'direct',
        label: 'Investments Purchased',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Investments purchased'],
        signConvention: 'outflowNegative',
    },
    investmentsSold: {
        id: 'investmentsSold',
        type: 'direct',
        label: 'Investments Sold',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Investments sold'],
        signConvention: 'inflowPositive',
    },
    interestReceived: {
        id: 'interestReceived',
        type: 'direct',
        label: 'Interest Received',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Interest received'],
        signConvention: 'inflowPositive',
    },
    dividendsReceived: {
        id: 'dividendsReceived',
        type: 'direct',
        label: 'Dividends Received',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Dividends received'],
        signConvention: 'inflowPositive',
    },
    otherInvestingItems: {
        id: 'otherInvestingItems',
        type: 'direct',
        label: 'Other Investing Items',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Other investing items'],
    },
    proceedsFromShares: {
        id: 'proceedsFromShares',
        type: 'direct',
        label: 'Proceeds from Shares',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Proceeds from shares'],
        signConvention: 'inflowPositive',
    },
    proceedsFromBorrowings: {
        id: 'proceedsFromBorrowings',
        type: 'direct',
        label: 'Proceeds from Borrowings',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Proceeds from borrowings'],
        signConvention: 'inflowPositive',
    },
    repaymentOfBorrowings: {
        id: 'repaymentOfBorrowings',
        type: 'direct',
        label: 'Repayment of Borrowings',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Repayment of borrowings'],
        signConvention: 'outflowNegative',
    },
    interestPaidFinancing: {
        id: 'interestPaidFinancing',
        type: 'direct',
        label: 'Interest Paid',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Interest paid fin'],
        signConvention: 'outflowNegative',
    },
    dividendsPaid: {
        id: 'dividendsPaid',
        type: 'direct',
        label: 'Dividends Paid',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Dividends paid'],
        signConvention: 'outflowNegative',
    },
    financialLiabilitiesCashFlow: {
        id: 'financialLiabilitiesCashFlow',
        type: 'direct',
        label: 'Financial Liabilities',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Financial liabilities'],
    },
    otherFinancingItems: {
        id: 'otherFinancingItems',
        type: 'direct',
        label: 'Other Financing Items',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Other financing items'],
    },
    profitFromOperationsCashFlow: {
        id: 'profitFromOperationsCashFlow',
        type: 'direct',
        label: 'Profit from Operations',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Profit from operations'],
        auditOnly: true,
    },
    receivablesCashFlow: {
        id: 'receivablesCashFlow',
        type: 'direct',
        label: 'Receivables Movement',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Receivables'],
    },
    inventoryCashFlow: {
        id: 'inventoryCashFlow',
        type: 'direct',
        label: 'Inventory Movement',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Inventory'],
    },
    payablesCashFlow: {
        id: 'payablesCashFlow',
        type: 'direct',
        label: 'Payables Movement',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Payables'],
    },
    loansAdvancesCashFlow: {
        id: 'loansAdvancesCashFlow',
        type: 'direct',
        label: 'Loans and Advances Movement',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Loans Advances'],
    },
    otherWorkingCapitalItems: {
        id: 'otherWorkingCapitalItems',
        type: 'direct',
        label: 'Other Working Capital Items',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Other WC items'],
    },
    workingCapitalChanges: {
        id: 'workingCapitalChanges',
        type: 'direct',
        label: 'Working Capital Changes',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Working capital changes'],
    },
    directTaxes: {
        id: 'directTaxes',
        type: 'direct',
        label: 'Direct Taxes',
        unit: 'currency',
        statement: 'cashFlow',
        sourcePath: ['cashFlow', 'Direct taxes'],
        signConvention: 'outflowNegative',
    },

    // --- Calculated owner-earnings / quality metrics ---
    /**
     * Conservative FCF after gross fixed-asset purchases, before asset-sale proceeds.
     * Formula: cfo + fixedAssetsPurchased (purchases are stored as negative outflows).
     */
    grossCapexFcf: {
        id: 'grossCapexFcf',
        type: 'calculated',
        label: 'FCF Before Asset Sales',
        unit: 'currency',
        inputs: ['cfo', 'fixedAssetsPurchased'],
        compute: 'sum',
        explanation: 'Conservative FCF after gross fixed-asset purchases, before proceeds from asset sales.',
    },
    /**
     * Gross fixed-asset purchases less fixed-asset sale proceeds.
     * Formula: abs(fixedAssetsPurchased) - fixedAssetsSold.
     */
    netCapex: {
        id: 'netCapex',
        type: 'calculated',
        label: 'Net Fixed-Asset Capex',
        unit: 'currency',
        inputs: ['fixedAssetsPurchased', 'fixedAssetsSold'],
        compute: 'absMinus',
        explanation: 'Gross fixed-asset purchases less fixed-asset sale proceeds.',
    },
    /**
     * Share of operating cash remaining after net-capex FCF.
     * Formula: reportedFcf / cfo, displayed in percentage points.
     */
    fcfConversion: {
        id: 'fcfConversion',
        type: 'calculated',
        label: 'FCF Conversion',
        unit: 'percentage',
        inputs: ['reportedFcf', 'cfo'],
        compute: 'ratioPercent',
    },
    /**
     * Conservative FCF conversion excluding asset-sale proceeds.
     * Formula: grossCapexFcf / cfo, displayed in percentage points.
     */
    grossCapexFcfConversion: {
        id: 'grossCapexFcfConversion',
        type: 'calculated',
        label: 'FCF Before Asset Sales Conversion',
        unit: 'percentage',
        inputs: ['grossCapexFcf', 'cfo'],
        compute: 'ratioPercent',
    },
    /**
     * Gross capex intensity of operating cash.
     * Formula: abs(fixedAssetsPurchased) / cfo, displayed in percentage points.
     */
    capexToCfo: {
        id: 'capexToCfo',
        type: 'calculated',
        label: 'Capex / CFO',
        unit: 'percentage',
        inputs: ['fixedAssetsPurchased', 'cfo'],
        compute: 'ratioAbsNumeratorPercent',
    },
    /**
     * Reinvestment vs accounting depreciation.
     * Formula: abs(fixedAssetsPurchased) / depreciation.
     */
    capexToDepreciation: {
        id: 'capexToDepreciation',
        type: 'calculated',
        label: 'Capex / Depreciation',
        unit: 'multiple',
        inputs: ['fixedAssetsPurchased', 'depreciation'],
        compute: 'ratioAbsNumerator',
    },
    /**
     * Profit-to-cash conversion.
     * Formula: cfo / netProfit.
     */
    cfoToNetProfit: {
        id: 'cfoToNetProfit',
        type: 'calculated',
        label: 'CFO / Net Profit',
        unit: 'multiple',
        inputs: ['cfo', 'netProfit'],
        compute: 'ratio',
    },
    /**
     * Profit-to-cash conversion versus Indian EBIT.
     * Formula: cfo / (operatingProfit − depreciation).
     */
    cfoToEbit: {
        id: 'cfoToEbit',
        type: 'calculated',
        label: 'CFO / EBIT',
        unit: 'multiple',
        inputs: ['cfo', 'ebit'],
        compute: 'ratio',
        explanation: 'EBIT = Screener Operating Profit − Depreciation. Operating Profit is PBDIT, not EBIT.',
    },
    /**
     * Profit-to-cash conversion versus Indian/Screener EBITDA (Operating Profit).
     * Same identity as statement CFO/OP, expressed as a multiple.
     * Formula: cfo / operatingProfit.
     */
    cfoToEbitda: {
        id: 'cfoToEbitda',
        type: 'calculated',
        label: 'CFO / EBITDA',
        unit: 'multiple',
        inputs: ['cfo', 'operatingProfit'],
        compute: 'ratio',
        explanation: 'Screener Operating Profit is PBDIT (operating EBITDA). Matches statement CFO/OP as a multiple.',
    },
    /**
     * Share of CFO consumed by debt repayment.
     * Formula: abs(repaymentOfBorrowings) / cfo.
     */
    debtRepaymentToCfo: {
        id: 'debtRepaymentToCfo',
        type: 'calculated',
        label: 'Debt Repayment / CFO',
        unit: 'multiple',
        inputs: ['repaymentOfBorrowings', 'cfo'],
        compute: 'ratioAbsNumerator',
    },
    /**
     * Share of CFO paid out as cash dividends.
     * Formula: abs(dividendsPaid) / cfo.
     */
    dividendsToCfo: {
        id: 'dividendsToCfo',
        type: 'calculated',
        label: 'Dividends / CFO',
        unit: 'multiple',
        inputs: ['dividendsPaid', 'cfo'],
        compute: 'ratioAbsNumerator',
    },
    /**
     * Gross capex intensity of sales.
     * Formula: abs(fixedAssetsPurchased) / revenue, displayed in percentage points.
     */
    capexToSales: {
        id: 'capexToSales',
        type: 'calculated',
        label: 'Capex / Sales',
        unit: 'percentage',
        inputs: ['fixedAssetsPurchased', 'revenue'],
        compute: 'ratioAbsNumeratorPercent',
    },
    /**
     * Operating-cash coverage of total borrowings.
     * Formula: cfo / borrowings.
     */
    cfoToDebt: {
        id: 'cfoToDebt',
        type: 'calculated',
        label: 'CFO / Debt',
        unit: 'multiple',
        inputs: ['cfo', 'borrowings'],
        compute: 'ratio',
    },
    /**
     * Operating-cash coverage of current-liability proxy (Other Liabilities).
     * Formula: cfo / otherLiabilities. Distinct from cfoToDebt and dividendCoverage.
     */
    cashFlowCoverage: {
        id: 'cashFlowCoverage',
        type: 'calculated',
        label: 'Cash Flow Coverage',
        unit: 'multiple',
        inputs: ['cfo', 'otherLiabilities'],
        compute: 'ratio',
    },
    /**
     * Accounting profit vs cash remaining after net capex.
     * Formula: reportedFcf / netProfit.
     */
    fcfToNetProfit: {
        id: 'fcfToNetProfit',
        type: 'calculated',
        label: 'FCF / Net Profit',
        unit: 'multiple',
        inputs: ['reportedFcf', 'netProfit'],
        compute: 'ratio',
    },
    /**
     * Cash profitability versus revenue.
     * Formula: reportedFcf / revenue, displayed in percentage points.
     */
    fcfMargin: {
        id: 'fcfMargin',
        type: 'calculated',
        label: 'FCF Margin',
        unit: 'percentage',
        inputs: ['reportedFcf', 'revenue'],
        compute: 'ratioPercent',
    },
    /**
     * Conservative cash-profitability versus revenue.
     * Formula: grossCapexFcf / revenue, displayed in percentage points.
     */
    grossCapexFcfMargin: {
        id: 'grossCapexFcfMargin',
        type: 'calculated',
        label: 'FCF Before Asset Sales Margin',
        unit: 'percentage',
        inputs: ['grossCapexFcf', 'revenue'],
        compute: 'ratioPercent',
    },
    /**
     * Working-capital drag or support relative to CFO.
     * Formula: workingCapitalChanges / cfo, displayed in percentage points.
     * Interpret the sign using the native cash-flow convention.
     */
    workingCapitalToCfo: {
        id: 'workingCapitalToCfo',
        type: 'calculated',
        label: 'Working Capital Changes / CFO',
        unit: 'percentage',
        inputs: ['workingCapitalChanges', 'cfo'],
        compute: 'ratioPercent',
        warning: 'Interpret the sign using the native cash-flow convention. Positive changes support CFO; negative changes absorb cash.',
    },
    /**
     * Whether reported FCF covers cash dividends.
     * Formula: reportedFcf / abs(dividendsPaid).
     */
    dividendCoverage: {
        id: 'dividendCoverage',
        type: 'calculated',
        label: 'FCF Dividend Coverage',
        unit: 'multiple',
        inputs: ['reportedFcf', 'dividendsPaid'],
        compute: 'ratioAbsDenominator',
    },
    /**
     * Conservative FCF coverage of cash dividends.
     * Formula: grossCapexFcf / abs(dividendsPaid).
     */
    grossCapexFcfDividendCoverage: {
        id: 'grossCapexFcfDividendCoverage',
        type: 'calculated',
        label: 'Conservative FCF Dividend Coverage',
        unit: 'multiple',
        inputs: ['grossCapexFcf', 'dividendsPaid'],
        compute: 'ratioAbsDenominator',
    },
    /**
     * Net cash from borrowing activity (native signs).
     * Formula: proceedsFromBorrowings + repaymentOfBorrowings.
     */
    netDebtCashFlow: {
        id: 'netDebtCashFlow',
        type: 'calculated',
        label: 'Net Debt Cash Flow',
        unit: 'currency',
        inputs: ['proceedsFromBorrowings', 'repaymentOfBorrowings'],
        compute: 'sum',
    },
    /**
     * Net cash from investment-securities activity (native signs).
     * Formula: investmentsPurchased + investmentsSold.
     */
    netInvestmentCashFlow: {
        id: 'netInvestmentCashFlow',
        type: 'calculated',
        label: 'Net Investment Securities Cash Flow',
        unit: 'currency',
        inputs: ['investmentsPurchased', 'investmentsSold'],
        compute: 'sum',
    },

    // --- P&L / Balance Sheet calculated identities (statement math) ---
    netProfitMargin: {
        id: 'netProfitMargin',
        type: 'calculated',
        label: 'Net Profit Margin',
        unit: 'percentage',
        inputs: ['netProfit', 'revenue'],
        compute: 'ratioPercent',
        explanation: 'Net Profit / Revenue × 100.',
    },
    operatingProfitMarginPct: {
        id: 'operatingProfitMarginPct',
        type: 'calculated',
        label: 'Operating Profit Margin',
        unit: 'percentage',
        inputs: ['operatingProfit', 'revenue'],
        compute: 'ratioPercent',
        explanation: 'Operating Profit / Revenue × 100. Distinct from the statement OPM % line (operatingMargin).',
    },
    ebitdaMargin: {
        id: 'ebitdaMargin',
        type: 'calculated',
        label: 'EBITDA Margin',
        unit: 'percentage',
        inputs: ['ebitda', 'revenue'],
        compute: 'ratioPercent',
        explanation: '(Operating Profit + Depreciation) / Revenue × 100. Matches the P&L ratios table identity.',
    },
    totalEquity: {
        id: 'totalEquity',
        type: 'calculated',
        label: 'Total Equity',
        unit: 'currency',
        inputs: ['equityCapital', 'reserves'],
        compute: 'sum',
        explanation: 'Equity Capital + Reserves. Owner equity; non-controlling interest is not subtracted.',
    },
    equityMultiplier: {
        id: 'equityMultiplier',
        type: 'calculated',
        label: 'Equity Multiplier',
        unit: 'multiple',
        inputs: ['totalAssets', 'totalEquity'],
        compute: 'ratio',
        explanation: 'Total Assets / Total Equity. DuPont leverage component using ending balances.',
    },
    netDebt: {
        id: 'netDebt',
        type: 'calculated',
        label: 'Net Debt',
        unit: 'currency',
        inputs: ['borrowings', 'cashEquivalents'],
        compute: 'subtract',
        explanation: 'Borrowings − Cash Equivalents. Not floored at zero; a net-cash company is negative.',
    },
    netDebtToEbitda: {
        id: 'netDebtToEbitda',
        type: 'calculated',
        label: 'Net Debt / EBITDA',
        unit: 'multiple',
        inputs: ['netDebt', 'ebitda'],
        compute: 'ratioPositiveDenominator',
        explanation: 'Net Debt / (Operating Profit + Depreciation). Null when EBITDA is zero or negative.',
    },
    interestCoverageEbit: {
        id: 'interestCoverageEbit',
        type: 'calculated',
        label: 'Interest Coverage – EBIT',
        unit: 'multiple',
        inputs: ['operatingProfit', 'interestExpense'],
        compute: 'ratioAbsDenominator',
        explanation: 'Operating Profit / |Interest|. Matches the P&L ratios table ICR identity.',
    },
    roicWaccSpread: {
        id: 'roicWaccSpread',
        type: 'calculated',
        label: 'ROIC minus WACC',
        unit: 'percentage',
        inputs: ['operatingRoic', 'weightedAverageCostOfCapital'],
        compute: 'subtract',
        explanation: 'Operating ROIC − WACC, in percentage points. Negative spread means capital earned less than it cost.',
    },

    // --- P&L / Balance Sheet ratios from the API (average-balance / WACC formulas) ---
    returnOnEquity: {
        id: 'returnOnEquity',
        type: 'apiRatio',
        label: 'Return on Equity',
        unit: 'percentage',
        ratioName: 'returnOnEquity',
    },
    returnOnCapitalEmployed: {
        id: 'returnOnCapitalEmployed',
        type: 'apiRatio',
        label: 'Return on Capital Employed',
        unit: 'percentage',
        ratioName: 'returnOnCapitalEmployed',
    },
    returnOnAssets: {
        id: 'returnOnAssets',
        type: 'apiRatio',
        label: 'Return on Assets',
        unit: 'percentage',
        ratioName: 'returnOnAssets',
    },
    operatingRoic: {
        id: 'operatingRoic',
        type: 'apiRatio',
        label: 'Operating ROIC',
        unit: 'percentage',
        ratioName: 'operatingRoic',
    },
    weightedAverageCostOfCapital: {
        id: 'weightedAverageCostOfCapital',
        type: 'apiRatio',
        label: 'Weighted Average Cost of Capital',
        shortLabel: 'WACC',
        unit: 'percentage',
        ratioName: 'wacc',
    },
    debtToEquity: {
        id: 'debtToEquity',
        type: 'apiRatio',
        label: 'Debt to Equity',
        unit: 'multiple',
        ratioName: 'debtToEquity',
    },
    assetTurnoverRatio: {
        id: 'assetTurnoverRatio',
        type: 'apiRatio',
        label: 'Asset Turnover',
        unit: 'multiple',
        ratioName: 'assetTurnover',
    },
    inventoryTurnoverRatio: {
        id: 'inventoryTurnoverRatio',
        type: 'apiRatio',
        label: 'Inventory Turnover',
        unit: 'multiple',
        ratioName: 'inventoryTurnover',
    },
    receivablesTurnoverRatio: {
        id: 'receivablesTurnoverRatio',
        type: 'apiRatio',
        label: 'Receivables Turnover',
        unit: 'multiple',
        ratioName: 'receivablesTurnover',
    },

    /**
     * Cash Flow Ratios panel key. Display values are overlaid with reportedFcf
     * (Screener net-capex FCF) so they match the Cash Flow Statement.
     */
    fcfAbsolute: {
        id: 'fcfAbsolute',
        type: 'apiRatio',
        label: 'Free Cash Flow',
        unit: 'currency',
        ratioName: 'fcfAbsolute',
        warning: 'Panel cells overlay Screener net-capex FCF (CFO + fixed assets purchased + fixed assets sold).',
    },
    /**
     * Buffett-style owner earnings from the ratios API.
     * Backend: Net Profit + Depreciation + Amortization − |CapEx| − ΔWC.
     * Not identical to reportedFcf or grossCapexFcf. Absolute currency; not scored.
     */
    ownerEarnings: {
        id: 'ownerEarnings',
        type: 'apiRatio',
        label: 'Owner Earnings',
        unit: 'currency',
        ratioName: 'ownerEarnings',
        warning: 'API owner earnings is NI + D&A − |CapEx| − ΔWC. It is not reported FCF and is not given a sector currency threshold.',
    },
};

export const FINANCIAL_METRIC_IDS = Object.freeze(Object.keys(FINANCIAL_METRICS));

/**
 * Look up a registry metric by stable ID.
 *
 * @param {string} metricId
 * @returns {DirectMetric|CalculatedMetric|ApiRatioMetric|null}
 */
export function getFinancialMetric(metricId) {
    if (!metricId) return null;
    return FINANCIAL_METRICS[metricId] || null;
}

/**
 * Map a statement section + exact source label to a direct metric ID.
 *
 * @param {string} section
 * @param {string} label
 * @returns {string|null}
 */
export function findMetricIdBySourcePath(section, label) {
    if (!section || !label) return null;
    const match = Object.values(FINANCIAL_METRICS).find((metric) => (
        metric.type === 'direct'
        && metric.sourcePath
        && metric.sourcePath[0] === section
        && metric.sourcePath[1] === label
    ));
    return match ? match.id : null;
}

/**
 * Parse `financial.{section}.{label}` pseudo-ratio names from statement tables.
 *
 * @param {string} ratioName
 * @returns {{ section: string, label: string }|null}
 */
export function parseFinancialRatioName(ratioName) {
    if (!ratioName || typeof ratioName !== 'string') return null;
    const prefix = 'financial.';
    if (!ratioName.startsWith(prefix)) return null;
    const rest = ratioName.slice(prefix.length);
    const dot = rest.indexOf('.');
    if (dot <= 0) return null;
    return {
        section: rest.slice(0, dot),
        label: rest.slice(dot + 1),
    };
}

/**
 * Resolve the registry metric ID that best represents an opened ChartModal ratio.
 *
 * @param {string} [ratioName]
 * @returns {string|null}
 */
export function findMetricIdForRatioName(ratioName) {
    if (!ratioName) return null;
    if (FINANCIAL_METRICS[ratioName]) return ratioName;
    const parsed = parseFinancialRatioName(ratioName);
    if (parsed) return findMetricIdBySourcePath(parsed.section, parsed.label);
    const apiMatch = Object.values(FINANCIAL_METRICS).find((metric) => (
        metric.type === 'apiRatio' && metric.ratioName === ratioName
    ));
    return apiMatch ? apiMatch.id : null;
}
